import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiMessageCircle, FiX } from "react-icons/fi";
import { CommentAnchorType } from "../../../services/types";
import { CommentType } from "../../comments";
import {
  buildAnnotatedBlocks,
  getAnchorDisplayText,
  getReplyCount,
  renderBlockHtmlWithActiveAnchor,
} from "../utils/anchorText";
import {
  clearBrowserTextSelection,
  getAnchoredSelectionResult,
} from "../utils/selectionAnchor";
import type { AnchoredSelectionDraft } from "../utils/selectionAnchor";
import AnchorThreadPanel from "./AnchorThreadPanel";
import "./AnnotatedBody.css";

interface AnnotatedBodyProps {
  html: string;
  anchors: CommentAnchorType[];
  loading?: boolean;
  error?: unknown;
  commentsVisible?: boolean;
  gistId?: number | null;
  editId?: number | null;
  comments?: CommentType[];
  commentsLoading?: boolean;
  commentsError?: unknown;
  currentUserId?: number;
  currentUserRole?: string;
  canCreateAnchoredComment?: boolean;
  onCreateAnchoredComment?: (draft: AnchoredSelectionDraft, comment: string) => Promise<CommentAnchorType | void>;
  onReply?: (commentId: number, content: string) => Promise<void>;
  onSoftRemove?: (commentId: number) => Promise<void>;
  onHardDeleteThread?: (commentId: number) => Promise<void>;
  onAnchorOpen?: (anchorId: number) => void;
  activeAnchorId?: number | null;
}

function getAnchorAuthor(anchor: CommentAnchorType): string {
  return anchor.rootComment?.user?.name || anchor.createdBy?.name || "Anonymous";
}

function getAnchorPreview(anchor: CommentAnchorType): string {
  const raw = anchor.rootComment?.comment || "";
  const cleaned = raw.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  if (!cleaned) return "No comment text available.";
  return cleaned.length > 180 ? `${cleaned.slice(0, 180)}…` : cleaned;
}

function getAnchorThreadSize(anchor: CommentAnchorType): number {
  return 1 + getReplyCount(anchor);
}

function getAnchorListSummary(anchors: CommentAnchorType[]): { threadCount: number; replyCount: number; commentCount: number } {
  const replyCount = anchors.reduce((total, anchor) => total + getReplyCount(anchor), 0);
  return {
    threadCount: anchors.length,
    replyCount,
    commentCount: anchors.length + replyCount,
  };
}

function getAnchorExcerpt(anchor: CommentAnchorType): string {
  const text = getAnchorDisplayText(anchor).replace(/\s+/g, " ").trim();
  if (!text) return "Selected passage";
  return text.length > 92 ? `${text.slice(0, 92)}…` : text;
}

const AnchorPreviewCard: React.FC<{
  anchor: CommentAnchorType;
  mode: "hover" | "selected";
}> = ({ anchor, mode }) => {
  const replyCount = getReplyCount(anchor);

  return React.createElement(
    "div",
    { className: `anchored-comment-preview anchored-comment-preview-${mode}` },
    React.createElement("div", { className: "anchored-comment-preview-kicker" }, "Anchored discussion"),
    React.createElement("blockquote", { className: "anchored-comment-quote" }, `“${getAnchorDisplayText(anchor)}”`),
    React.createElement("div", { className: "anchored-comment-preview-body" }, getAnchorPreview(anchor)),
    React.createElement(
      "div",
      { className: "anchored-comment-preview-meta" },
      React.createElement("span", null, getAnchorAuthor(anchor)),
      React.createElement("span", null, "•"),
      React.createElement("span", null, `${replyCount} ${replyCount === 1 ? "reply" : "replies"}`),
      React.createElement("span", null, "•"),
      React.createElement("span", null, anchor.status.toLowerCase())
    ),
    mode === "hover"
      ? React.createElement(
          "div",
          { className: "anchored-comment-preview-hint" },
          "Click the marker to open the focused thread."
        )
      : null
  );
};

const AnchorMarker: React.FC<{
  anchor: CommentAnchorType;
  isActive: boolean;
  onHover: (id: number | null) => void;
  onSelect: (id: number) => void;
}> = ({ anchor, isActive, onHover, onSelect }) => {
  const replyCount = getReplyCount(anchor);

  return React.createElement(
    "button",
    {
      type: "button",
      className: `anchored-comment-marker ${isActive ? "is-active" : ""}`,
      onMouseEnter: () => onHover(anchor.id),
      onMouseLeave: () => onHover(null),
      onFocus: () => onHover(anchor.id),
      onBlur: () => onHover(null),
      onClick: () => onSelect(anchor.id),
      "aria-label": `Open anchored discussion: ${getAnchorDisplayText(anchor)}`,
      title: "Open anchored discussion",
    },
    React.createElement(FiMessageCircle, { "aria-hidden": "true" }),
    replyCount > 0
      ? React.createElement("span", { className: "anchored-comment-marker-count" }, replyCount)
      : null
  );
};

const AnnotatedBody: React.FC<AnnotatedBodyProps> = ({
  html,
  anchors,
  loading = false,
  error,
  commentsVisible = true,
  gistId,
  editId,
  comments = [],
  commentsLoading = false,
  commentsError,
  currentUserId,
  currentUserRole,
  canCreateAnchoredComment = false,
  onCreateAnchoredComment,
  onReply,
  onSoftRemove,
  onHardDeleteThread,
  onAnchorOpen,
  activeAnchorId: controlledActiveAnchorId = null,
}) => {
  const bodyBlocksRef = useRef<HTMLDivElement | null>(null);
  const selectionCaptureTimerRef = useRef<number | null>(null);

  const visibleAnchors = useMemo(
    () => (commentsVisible ? anchors.filter((anchor) => anchor.status !== "ARCHIVED") : []),
    [anchors, commentsVisible]
  );

  const { blocks, unplacedAnchors } = useMemo(
    () => buildAnnotatedBlocks(html, visibleAnchors),
    [html, visibleAnchors]
  );

  const [hoveredAnchorId, setHoveredAnchorId] = useState<number | null>(null);
  const [selectedAnchorId, setSelectedAnchorId] = useState<number | null>(null);
  const [openClusterBlockIndex, setOpenClusterBlockIndex] = useState<number | null>(null);
  const [selectionDraft, setSelectionDraft] = useState<AnchoredSelectionDraft | null>(null);
  const [selectionComment, setSelectionComment] = useState<string>("");
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
  const [isCreatingAnchor, setIsCreatingAnchor] = useState<boolean>(false);
  const [composerPosition, setComposerPosition] = useState<{
    top: number;
    left: number;
    isPlacementBelow: boolean;
  } | null>(null);

  const displayedActiveAnchorId = hoveredAnchorId ?? controlledActiveAnchorId ?? selectedAnchorId;
  const selectedAnchor = visibleAnchors.find((anchor) => anchor.id === selectedAnchorId) || null;
  const hoveredAnchor = visibleAnchors.find((anchor) => anchor.id === hoveredAnchorId) || null;

  const handleSelectAnchor = (anchorId: number) => {
    setOpenClusterBlockIndex(null);
    if (onAnchorOpen) {
      setHoveredAnchorId(null);
      onAnchorOpen(anchorId);
      return;
    }

    setSelectedAnchorId((previousAnchorId) => (previousAnchorId === anchorId ? null : anchorId));
  };

  const handleSelectClusterAnchor = (anchorId: number) => {
    setHoveredAnchorId(null);
    setOpenClusterBlockIndex(null);
    if (onAnchorOpen) {
      onAnchorOpen(anchorId);
      return;
    }

    setSelectedAnchorId(anchorId);
  };

  const resetSelectionComposer = useCallback(() => {
    setSelectionDraft(null);
    setSelectionComment("");
    setSelectionError(null);
    setSelectionNotice(null);
    setComposerPosition(null);
    clearBrowserTextSelection();
  }, []);

  const captureSelection = useCallback(() => {
    if (!canCreateAnchoredComment || !onCreateAnchoredComment || isCreatingAnchor) return;

    const result = getAnchoredSelectionResult(bodyBlocksRef.current);
    if (!result.draft) {
      if (result.issue) {
        setSelectionDraft(null);
        setSelectionComment("");
        setSelectionError(null);
        setSelectionNotice(result.issue.message);
        setComposerPosition(null);
      }

      // Browser selection events are noisy: React re-renders, toolbar clicks,
      // focus changes, and styled inline spans can emit a late collapsed/empty
      // selectionchange after a valid selection was captured. Do not clear an
      // existing composer unless we have a real diagnostic issue or the user
      // explicitly cancels it.
      return;
    }

    setSelectionDraft(result.draft);
    setSelectionComment("");
    setSelectionError(null);
    setSelectionNotice(null);
    setSelectedAnchorId(null);
    setOpenClusterBlockIndex(null);

    // Desktop composer coordinates are fixed to the viewport, not trapped inside
    // the current GIST card. This keeps the composer readable when the selection
    // is close to the bottom edge and the next GIST/card visually overlaps below.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && typeof window !== "undefined" && window.innerWidth > 720) {
      const range = selection.getRangeAt(0);

      const rects = Array.from(range.getClientRects());
      let lastRect = range.getBoundingClientRect();
      if (rects.length > 0) {
        for (let i = rects.length - 1; i >= 0; i--) {
          if (rects[i].width > 0 && rects[i].height > 0) {
            lastRect = rects[i];
            break;
          }
        }
      }

      const rangeBoundingRect = range.getBoundingClientRect();
      const viewportPadding = 16;
      const verticalGap = 12;
      const composerWidth = Math.min(420, Math.max(280, window.innerWidth - viewportPadding * 2));
      const composerHeightEstimate = Math.min(360, Math.max(260, Math.round(window.innerHeight * 0.44)));

      const belowTop = lastRect.bottom + verticalGap;
      const aboveTop = rangeBoundingRect.top - composerHeightEstimate - verticalGap;
      const hasRoomBelow = belowTop + composerHeightEstimate <= window.innerHeight - viewportPadding;
      const hasRoomAbove = aboveTop >= viewportPadding;

      let top = belowTop;
      let isPlacementBelow = true;

      if (!hasRoomBelow && hasRoomAbove) {
        top = aboveTop;
        isPlacementBelow = false;
      } else if (!hasRoomBelow) {
        top = Math.max(viewportPadding, window.innerHeight - composerHeightEstimate - viewportPadding);
        isPlacementBelow = top >= lastRect.bottom;
      }

      const preferredLeft = Math.min(lastRect.left, rangeBoundingRect.right - composerWidth);
      const left = Math.max(
        viewportPadding,
        Math.min(window.innerWidth - composerWidth - viewportPadding, preferredLeft)
      );

      setComposerPosition({
        top: Math.round(top),
        left: Math.round(left),
        isPlacementBelow,
      });
    } else {
      setComposerPosition(null);
    }

  }, [canCreateAnchoredComment, isCreatingAnchor, onCreateAnchoredComment]);

  const scheduleSelectionCapture = useCallback(
    (delay = 0) => {
      if (typeof window === "undefined") return;

      if (selectionCaptureTimerRef.current !== null) {
        window.clearTimeout(selectionCaptureTimerRef.current);
      }

      selectionCaptureTimerRef.current = window.setTimeout(() => {
        selectionCaptureTimerRef.current = null;
        captureSelection();
      }, delay);
    },
    [captureSelection]
  );

  useEffect(() => {
    if (!canCreateAnchoredComment || !onCreateAnchoredComment) return undefined;

    const handleSelectionChange = () => {
      scheduleSelectionCapture(140);
    };

    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      if (selectionCaptureTimerRef.current !== null) {
        window.clearTimeout(selectionCaptureTimerRef.current);
        selectionCaptureTimerRef.current = null;
      }
    };
  }, [canCreateAnchoredComment, onCreateAnchoredComment, scheduleSelectionCapture]);

  // Outside-click listener to dismiss the selection draft
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!selectionDraft) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      const insideBody = bodyBlocksRef.current?.contains(target);
      const insideComposer = target.closest(".anchored-comment-selection-composer");
      const insideThreadPanel = target.closest(".anchored-comment-thread-panel");

      if (!insideBody && !insideComposer && !insideThreadPanel) {
        resetSelectionComposer();
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [selectionDraft, resetSelectionComposer]);

  const handleCreateAnchoredComment = async () => {
    if (!selectionDraft || !onCreateAnchoredComment) return;

    const comment = selectionComment.trim();
    if (!comment) {
      setSelectionError("Write a comment before creating an anchored discussion.");
      return;
    }

    try {
      setIsCreatingAnchor(true);
      setSelectionError(null);
      const createdAnchor = await onCreateAnchoredComment(selectionDraft, comment);
      resetSelectionComposer();
      if (createdAnchor?.id) {
        setHoveredAnchorId(null);
        setOpenClusterBlockIndex(null);
        if (onAnchorOpen) {
          onAnchorOpen(createdAnchor.id);
        } else {
          setSelectedAnchorId(createdAnchor.id);
        }
      }
    } catch (error: any) {
      console.error("Failed to create anchored comment", error);
      setSelectionError(error?.message || "Failed to create anchored comment. Please try again.");
    } finally {
      setIsCreatingAnchor(false);
    }
  };

  const canRenderThreadPanel = Boolean(selectedAnchor && gistId);

  const statusNodes = [
    loading
      ? React.createElement("div", { key: "loading", className: "annotated-body-status" }, "Loading anchored discussions…")
      : null,
    error
      ? React.createElement(
          "div",
          { key: "error", className: "annotated-body-status annotated-body-status-warning" },
          "Anchored discussion markers are temporarily unavailable. The Gist body is still readable."
        )
      : null,
  ];

  const blockNodes = blocks.map((block) => {
    const isHighlighted = block.anchors.some((anchor) => anchor.id === displayedActiveAnchorId);

    const blockSummary = getAnchorListSummary(block.anchors);
    const hasOpenCluster = openClusterBlockIndex === block.index;
    const hasActiveAnchorInBlock = block.anchors.some((anchor) => anchor.id === displayedActiveAnchorId);

    const markerRail =
      block.anchors.length > 0
        ? React.createElement(
            "div",
            {
              className: `anchored-comment-marker-rail ${block.anchors.length > 1 ? "is-clustered" : ""}`,
              "aria-label":
                block.anchors.length > 1
                  ? `${blockSummary.threadCount} anchored discussions in this block`
                  : "Anchored discussion marker",
            },
            block.anchors.length === 1
              ? block.anchors.map((anchor) =>
                  React.createElement(
                    "span",
                    { key: anchor.id, className: "anchored-comment-marker-wrap" },
                    React.createElement(AnchorMarker, {
                      anchor,
                      isActive: anchor.id === displayedActiveAnchorId,
                      onHover: setHoveredAnchorId,
                      onSelect: handleSelectAnchor,
                    }),
                    hoveredAnchorId === anchor.id
                      ? React.createElement(
                          "div",
                          { className: "anchored-comment-hover-popover", role: "status" },
                          React.createElement(AnchorPreviewCard, { anchor, mode: "hover" })
                        )
                      : null
                  )
                )
              : React.createElement(
                  "span",
                  { className: "anchored-comment-marker-wrap anchored-comment-cluster-wrap" },
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `anchored-comment-marker anchored-comment-marker-cluster ${
                        hasActiveAnchorInBlock || hasOpenCluster ? "is-active" : ""
                      }`,
                      onClick: () =>
                        setOpenClusterBlockIndex((previousIndex) =>
                          previousIndex === block.index ? null : block.index
                        ),
                      "aria-label": `${blockSummary.threadCount} anchored discussions, ${blockSummary.replyCount} replies total`,
                      title: `${blockSummary.threadCount} anchored discussions in this block`,
                    },
                    React.createElement(FiMessageCircle, { "aria-hidden": "true" }),
                    React.createElement(
                      "span",
                      { className: "anchored-comment-cluster-number" },
                      blockSummary.threadCount
                    )
                  ),
                  hasOpenCluster
                    ? React.createElement(
                        "div",
                        { className: "anchored-comment-cluster-popover", role: "dialog", "aria-label": "Anchored discussions in this block" },
                        React.createElement(
                          "div",
                          { className: "anchored-comment-cluster-title" },
                          `${blockSummary.threadCount} anchored discussions`
                        ),
                        React.createElement(
                          "div",
                          { className: "anchored-comment-cluster-meta" },
                          `${blockSummary.commentCount} total ${blockSummary.commentCount === 1 ? "comment" : "comments"}`
                        ),
                        block.anchors.map((anchor) => {
                          const replyCount = getReplyCount(anchor);

                          return React.createElement(
                            "button",
                            {
                              key: anchor.id,
                              type: "button",
                              className: `anchored-comment-cluster-item ${anchor.id === displayedActiveAnchorId ? "is-active" : ""}`,
                              onMouseEnter: () => setHoveredAnchorId(anchor.id),
                              onMouseLeave: () => setHoveredAnchorId(null),
                              onClick: () => handleSelectClusterAnchor(anchor.id),
                            },
                            React.createElement("span", { className: "anchored-comment-cluster-quote" }, `“${getAnchorExcerpt(anchor)}”`),
                            React.createElement(
                              "span",
                              { className: "anchored-comment-cluster-item-meta" },
                              `${getAnchorAuthor(anchor)} · ${getAnchorThreadSize(anchor)} ${
                                getAnchorThreadSize(anchor) === 1 ? "comment" : "comments"
                              }${replyCount > 0 ? ` (${replyCount} ${replyCount === 1 ? "reply" : "replies"})` : ""}`
                            )
                          );
                        })
                      )
                    : null
                )
          )
        : null;

    return React.createElement(
      "div",
      {
        key: `annotated-block-${block.index}`,
        className: `annotated-body-block ${block.anchors.length > 0 ? "has-anchored-comments" : ""} ${isHighlighted ? "is-highlighted" : ""}`,
        "data-block-index": block.index,
      },
      React.createElement("div", {
        className: "annotated-body-block-content",
        dangerouslySetInnerHTML: { __html: renderBlockHtmlWithActiveAnchor(block, displayedActiveAnchorId) },
      }),
      markerRail
    );
  });

  const composerStyle: React.CSSProperties = {};
  if (composerPosition && typeof window !== "undefined" && window.innerWidth > 720) {
    composerStyle.top = `${composerPosition.top}px`;
    composerStyle.left = `${composerPosition.left}px`;
  }

  const selectionComposer =
    selectionDraft && canCreateAnchoredComment
      ? React.createElement(
          "aside",
          {
            className: `anchored-comment-selection-composer ${composerPosition?.isPlacementBelow === false ? "is-above-selection" : "is-below-selection"}`,
            style: composerStyle,
            "aria-label": "Create anchored comment",
          },
          React.createElement(
            "div",
            { className: "anchored-comment-selection-header" },
            React.createElement(
              "div",
              null,
              React.createElement("div", { className: "anchored-comment-selection-kicker" }, "New anchored discussion"),
              React.createElement("blockquote", null, `“${selectionDraft.selectedText}”`)
            ),
            React.createElement(
              "button",
              {
                type: "button",
                className: "anchored-comment-selection-close",
                onClick: resetSelectionComposer,
                "aria-label": "Cancel anchored comment",
                title: "Cancel anchored comment",
              },
              React.createElement(FiX, { "aria-hidden": "true" })
            )
          ),
          React.createElement("textarea", {
            className: "anchored-comment-selection-input",
            value: selectionComment,
            disabled: isCreatingAnchor,
            rows: 4,
            placeholder: "Write a comment about this selected passage…",
            onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => setSelectionComment(event.target.value),
          }),
          selectionError
            ? React.createElement("div", { className: "anchored-comment-selection-error" }, selectionError)
            : null,
          React.createElement(
            "div",
            { className: "anchored-comment-selection-actions" },
            React.createElement(
              "button",
              {
                type: "button",
                className: "anchored-comment-selection-secondary",
                onClick: resetSelectionComposer,
                disabled: isCreatingAnchor,
              },
              "Cancel"
            ),
            React.createElement(
              "button",
              {
                type: "button",
                className: "anchored-comment-selection-primary",
                onClick: handleCreateAnchoredComment,
                disabled: isCreatingAnchor || !selectionComment.trim(),
              },
              isCreatingAnchor ? "Creating…" : "Create anchored comment"
            )
          )
        )
      : null;

  const selectionNoticeNode =
    selectionNotice && !selectionDraft
      ? React.createElement(
          "div",
          { className: "anchored-comment-selection-notice", role: "status", "aria-live": "polite" },
          selectionNotice
        )
      : null;

  const loginHint =
    !canCreateAnchoredComment && commentsVisible
      ? React.createElement(
          "div",
          { className: "anchored-comment-selection-hint" },
          "Login to select text and start an anchored discussion."
        )
      : null;

  const unplacedList =
    unplacedAnchors.length > 0
      ? React.createElement(
          "div",
          { className: "anchored-comment-unplaced-list" },
          React.createElement("div", { className: "anchored-comment-unplaced-title" }, "Older or unplaced anchored discussions"),
          unplacedAnchors.map((anchor) =>
            React.createElement(
              "button",
              {
                key: anchor.id,
                type: "button",
                className: `anchored-comment-unplaced-item ${anchor.id === displayedActiveAnchorId ? "is-active" : ""}`,
                onMouseEnter: () => setHoveredAnchorId(anchor.id),
                onMouseLeave: () => setHoveredAnchorId(null),
                onClick: () => handleSelectAnchor(anchor.id),
              },
              React.createElement(FiMessageCircle, { "aria-hidden": "true" }),
              React.createElement("span", null, getAnchorDisplayText(anchor))
            )
          )
        )
      : null;

  const hoverPanel =
    !selectedAnchor && hoveredAnchor
      ? React.createElement(
          "aside",
          { className: "anchored-comment-selected-panel", "aria-live": "polite" },
          React.createElement(AnchorPreviewCard, { anchor: hoveredAnchor, mode: "selected" })
        )
      : null;

  const threadPanel =
    canRenderThreadPanel && selectedAnchor
      ? React.createElement(AnchorThreadPanel, {
          anchor: selectedAnchor,
          comments,
          gistId: gistId as number,
          editId,
          loading: commentsLoading,
          error: commentsError,
          currentUserId,
          currentUserRole,
          onClose: () => setSelectedAnchorId(null),
          onReply,
          onSoftRemove,
          onHardDeleteThread,
        })
      : null;

  return React.createElement(
    "div",
    { className: "annotated-body-shell" },
    React.createElement(
      "div",
      { className: "annotated-body-card" },
      ...statusNodes,
      React.createElement(
        "div",
        {
          ref: bodyBlocksRef,
          className: "annotated-body-blocks ql-editor",
          "aria-label": "Gist body with anchored discussion markers",
          onMouseUp: () => scheduleSelectionCapture(0),
          onKeyUp: () => scheduleSelectionCapture(0),
          onPointerUp: () => scheduleSelectionCapture(80),
          onTouchEnd: () => scheduleSelectionCapture(180),
        },
        blockNodes
      ),
      selectionComposer,
      selectionNoticeNode,
      loginHint,
      unplacedList,
      hoverPanel,
      threadPanel
    )
  );
};

export default AnnotatedBody;
